import { apiRequest, toQueryString, getToken, getApiUrl, notifySessionExpired } from './apiRequest';

export const repositoryAPI = {
  getById: async (id) => {
    return await apiRequest<any>(`/api/repositories/${id}`, {
      method: 'GET',
    });
  },

  sync: async (id) => {
    return await apiRequest<any>(`/api/repositories/${id}/sync`, {
      method: 'POST',
    });
  },

  /** Queue code index for this repo (requires api.repositories.codeIndex.trigger; repo must be on a project). */
  triggerCodeIndex: async (id) => {
    return await apiRequest<any>(`/api/repositories/${encodeURIComponent(id)}/code-index/trigger`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  /** Chunk count, commit, branch (requires api.repositories.codeIndex.stats.read). */
  getCodeIndexStats: async (id) => {
    return await apiRequest<any>(`/api/repositories/${encodeURIComponent(id)}/code-index/stats`, {
      method: 'GET',
    });
  },

  /** Cancel queued or in-flight index (requires api.repositories.codeIndex.cancel). */
  cancelCodeIndex: async (id) => {
    return await apiRequest<any>(`/api/repositories/${encodeURIComponent(id)}/code-index/cancel`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  cancelSync: async (id) => {
    return await apiRequest<any>(`/api/repositories/${id}/sync/cancel`, {
      method: 'POST',
    });
  },

  /** Continue older commit history past the normal sync page cap. */
  backfillCommits: async (id) => {
    return await apiRequest<any>(`/api/repositories/${id}/backfill-commits`, {
      method: 'POST',
    });
  },

  /** Continue older PR list past the normal sync page cap. */
  backfillPullRequests: async (id) => {
    return await apiRequest<any>(`/api/repositories/${id}/backfill-pull-requests`, {
      method: 'POST',
    });
  },

  /** Commits + PRs + forced tech stack scan. */
  backfillAll: async (id) => {
    return await apiRequest<any>(`/api/repositories/${id}/backfill-all`, {
      method: 'POST',
    });
  },

  /** GitHub's own commit/PR totals via GraphQL, for comparison against the locally-mirrored counts. */
  fetchTrueCounts: async (id) => {
    return await apiRequest<any>(`/api/repositories/${id}/true-counts`, {
      method: 'POST',
    });
  },

  delete: async (id) => {
    return await apiRequest<any>(`/api/repositories/${id}`, {
      method: 'DELETE',
    });
  },

  getTechStack: async (id) => {
    return await apiRequest<any>(`/api/repositories/${id}/tech-stack`, {
      method: 'GET',
    });
  },

  scanTechStack: async (id) => {
    return await apiRequest<any>(`/api/repositories/${id}/scan-tech-stack`, {
      method: 'POST',
    });
  },

  getBranches: async (id, params: any = {}) => {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const url = qs.toString()
      ? `/api/repositories/${id}/branches?${qs.toString()}`
      : `/api/repositories/${id}/branches`;
    return await apiRequest<any>(url, { method: 'GET' });
  },

  getCommits: async (id, params: any = {}) => {
    const qs = toQueryString(params);
    const data = await apiRequest<any>(`/api/repositories/${id}/commits${qs}`, { method: 'GET' });
    return { commits: data.commits ?? [], meta: data.meta };
  },

  getActivity: async (id, params: any = {}) => {
    const qs = toQueryString(params);
    const data = await apiRequest<any>(`/api/repositories/${id}/activity${qs}`, { method: 'GET' });
    return data.activities ?? [];
  },

  getPullRequests: async (id, params: any = {}) => {
    const qs = toQueryString(params);
    const data = await apiRequest<any>(`/api/repositories/${id}/pull-requests${qs}`, { method: 'GET' });
    return { pullRequests: data.pullRequests ?? [], stats: data.stats };
  },

  getStatistics: async (id) => {
    const data = await apiRequest<any>(`/api/repositories/${id}/statistics`, { method: 'GET' });
    return data.statistics ?? data;
  },

  getDora: async (id, windowDays = 30) => {
    const qs = toQueryString({ windowDays });
    const data = await apiRequest<any>(`/api/repositories/${encodeURIComponent(id)}/dora${qs}`, {
      method: 'GET',
    });
    return { dora: data.dora, meta: data.meta };
  },

  getDoraLabelMetrics: async (id, options: any = {}) => {
    const params: any = {};
    if (options.windowDays != null) params.windowDays = options.windowDays;
    if (options.granularity === 'week') params.granularity = 'week';
    const qs = toQueryString(params);
    const data = await apiRequest<any>(
      `/api/repositories/${encodeURIComponent(id)}/dora-label-metrics${qs}`,
      { method: 'GET' }
    );
    return { labelDora: data.labelDora, meta: data.meta };
  },

  getCycleTime: async (id, options: any = {}) => {
    const params = new URLSearchParams();
    if (options.windowDays != null) params.set('windowDays', String(options.windowDays));
    const qs = params.toString();
    const data = await apiRequest<any>(
      `/api/repositories/${encodeURIComponent(id)}/cycle-time${qs ? `?${qs}` : ''}`,
      { method: 'GET' }
    );
    return { cycleTime: data.cycleTime, meta: data.meta };
  },

  downloadDoraLabelsTemplate: async (id) => {
    const token = getToken();
    const base = getApiUrl();
    const res = await fetch(
      `${base}/api/repositories/${encodeURIComponent(id)}/dora-labels-template`,
      {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (res.status === 401 && token) {
      notifySessionExpired();
      throw new Error('Session expired. Please log in again.');
    }
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      throw new Error(errText || `Download failed (${res.status})`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dora-labels.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  getChurn: async (id, windowDays = 30) => {
    const qs = toQueryString({ windowDays });
    const data = await apiRequest<any>(`/api/repositories/${encodeURIComponent(id)}/churn${qs}`, {
      method: 'GET',
    });
    const churn = data.churn ?? data;
    return { dates: churn.dates ?? [], ratios: churn.ratios ?? [] };
  },

  update: async (id, data) => {
    const res = await apiRequest<any>(`/api/repositories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return res;
  },

  listKnowledgeSources: async (id) => {
    const data = await apiRequest<any>(`/api/repositories/${encodeURIComponent(id)}/knowledge-sources`, {
      method: 'GET',
    });
    return data.sources ?? [];
  },
  createKnowledgeUrl: async (id, body) => {
    const data = await apiRequest<any>(`/api/repositories/${encodeURIComponent(id)}/knowledge-sources`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return data.source ?? null;
  },
  uploadKnowledgeFile: async (id, file) => {
    const form = new FormData();
    form.append('file', file);
    const data = await apiRequest<any>(
      `/api/repositories/${encodeURIComponent(id)}/knowledge-sources/upload`,
      {
        method: 'POST',
        body: form,
      }
    );
    return data.source ?? null;
  },
  patchKnowledgeSource: async (id, sourceId, body) => {
    const data = await apiRequest<any>(
      `/api/repositories/${encodeURIComponent(id)}/knowledge-sources/${encodeURIComponent(sourceId)}`,
      { method: 'PATCH', body: JSON.stringify(body) }
    );
    return data.source ?? null;
  },
  deleteKnowledgeSource: async (id, sourceId) => {
    await apiRequest<any>(
      `/api/repositories/${encodeURIComponent(id)}/knowledge-sources/${encodeURIComponent(sourceId)}`,
      {
        method: 'DELETE',
      }
    );
  },
  reprocessKnowledgeSource: async (id, sourceId) => {
    const data = await apiRequest<any>(
      `/api/repositories/${encodeURIComponent(id)}/knowledge-sources/${encodeURIComponent(sourceId)}/reprocess`,
      { method: 'POST', body: JSON.stringify({}) }
    );
    return data.source ?? null;
  },
};
